
import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { APIError, useAxios } from '../../core/_axios';
import { Service, ServiceFamilyMember } from '../../core/_models';
import {
    createGeneratedPDF,
    getServiceFamilyMembersByUniqueIdentifier,
    resendFamilyMemberInvitationByUniqueIdentifier
} from '../../core/_requests';
import usePageTitle from '../../hooks/usePageTitle';
import LoadingSpinner from '../../components/LoadingSpinner';
import FuneralHomeLogoUploader from '../../components/FuneralHomeLogoUploader';

export default function SendServiceToFamily({
    service,
}: {
    service: Service,
}) {
    const axios = useAxios();

    usePageTitle('Send Service to Family');

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);
    const [familyMembers, setFamilyMembers] = useState<ServiceFamilyMember[]>([]);

    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const [generatingBook, setGeneratingBook] = useState(false);
    const [errorGeneratingBook, setErrorGeneratingBook] = useState<string | null>(null);

    const [requestingNewPDF, setRequestingNewPDF] = useState(false);

    const [showLogoNotice, setShowLogoNotice] = useState(
        !service.account.public_data.friends_and_relatives_pdf_footer_logo_url   // show notice only if missing
    );
    const [showLogoUploader, setShowLogoUploader] = useState(false);

    const loadServiceFamilyMembers = useCallback(async () => {
        setLoading(true);
        setLoadingError(null);
        try {
            const {
                familyMembers: updatedFamilyMembers,
                familyMembersCount
            } = await getServiceFamilyMembersByUniqueIdentifier(axios, service.unique_identifier);
            setFamilyMembers(updatedFamilyMembers);
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setLoadingError(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [axios, service.unique_identifier]);

    const onResendFamilyMemberInvitation = useCallback(async (familyMember: ServiceFamilyMember) => {
        setLoading(true);
        setLoadingError(null);
        setSuccessMessage(null);
        try {
            await resendFamilyMemberInvitationByUniqueIdentifier(axios, service.unique_identifier, familyMember.unique_identifier);
            setSuccessMessage(`Invitation resent to ${familyMember.full_name}.`);
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setLoadingError(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [axios, service.unique_identifier]);

    const onGenerateDigitalDownloadClicked = useCallback(async () => {
        if (generatingBook) {
            return;
        }
        setGeneratingBook(true);
        setErrorGeneratingBook(null);
        try {
            const resultingBook = await createGeneratedPDF(axios, {
                service_unique_id: service.unique_identifier,
                create_pdf_for_digital_download: true
            })
            // wait 10 seconds for the book to be generated
            setTimeout(() => {
                //reload:
                window.location.reload();
            }, 10000);
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setErrorGeneratingBook(errorMessage)
            setGeneratingBook(false)
        }
    }, [axios, service.unique_identifier]);

    useEffect(() => {
        loadServiceFamilyMembers();
    }, [loadServiceFamilyMembers]);

    if (loading) {
        return (
            <div className="container-fixed">
                <LoadingSpinner />
            </div>
        );
    }

    if (loadingError) {
        return (
            <div className="container-fixed">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">
                        {loadingError}
                    </span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={loadServiceFamilyMembers}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return <>
        <div>
            <div>
                {showLogoNotice && (
                    <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded relative mb-5" role="alert">
                        <button
                            type="button"
                            className="absolute top-2 right-2 text-blue-700 hover:text-blue-900"
                            aria-label="Dismiss"
                            onClick={() => setShowLogoNotice(false)}
                        >
                            &times;
                        </button>
                        <div>
                            <span>No funeral-home logo has been added to customise the generated PDF.</span>
                        </div>
                        {!showLogoUploader && (
                            <div className="mt-2">
                                <button
                                    type="button"
                                    className="text-blue-700 underline hover:text-blue-900"
                                    onClick={() => setShowLogoUploader(true)}
                                >
                                    Add a logo
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
            {showLogoUploader && (
                <div className="mb-5">
                    <FuneralHomeLogoUploader
                        account={service.account}
                        onClose={() => setShowLogoUploader(false)}
                    />
                </div>
            )}
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 lg:gap-7.5">
            <div className="col-span-2">
                <div className="flex flex-col gap-5 lg:gap-7.5">
                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title">
                                {`Download ${service.public_data.deceased_person.full_name}'s Service as PDF`}
                            </h3>
                        </div>
                        {(requestingNewPDF || !service.public_data.latest_digital_download_pdf_url) ? <div className="card-body grid gap-7.5 py-5 lg:py-7.5">
                            <div>
                                <button
                                    disabled={generatingBook}
                                    className="btn btn-lg btn-secondary" onClick={onGenerateDigitalDownloadClicked}>
                                    Generate PDF
                                </button>
                            </div>
                            {generatingBook && <div className="mt-3">
                                <div className="text-lg font-medium">
                                    Generating PDF (this takes about 10 seconds)...
                                </div>
                            </div>}
                        </div> : <div className="card-body grid gap-7.5 py-5 lg:py-7.5">
                            <div>
                                <a
                                    href={service.public_data.latest_digital_download_pdf_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-lg btn-primary">
                                    Download PDF
                                </a>
                            </div>
                            <div className="text-lg font-medium">
                                Have new signatures been added since the last PDF was generated? If so, please generate a new PDF.
                            </div>
                            <div>
                                <button
                                    className="btn btn-lg btn-secondary"
                                    onClick={() => {
                                        setRequestingNewPDF(true)
                                        onGenerateDigitalDownloadClicked();
                                    }}>
                                    Generate New PDF
                                </button>
                            </div>
                        </div>}
                    </div>
                    <div className="ml-4">
                        or
                    </div>
                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title">
                                {`Send ${service.public_data.deceased_person.full_name}'s Service to Family as Digital Link`}
                            </h3>
                        </div>
                        <div className="card-body grid gap-7.5 py-5 lg:py-7.5">
                            {familyMembers.length === 0 ? <div>
                                <div className="text-lg font-medium">
                                    {`No family members have been added to ${service.public_data.deceased_person.full_name}'s service.`}
                                </div>
                            </div> : <>
                                {successMessage && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
                                    <span className="block sm:inline">
                                        {successMessage}
                                    </span>
                                </div>}
                                <div>
                                    <p>
                                        Family Members:
                                    </p>
                                </div>
                                <div>
                                    {familyMembers.map((familyMember) => {
                                        return <div key={familyMember.unique_identifier} className="flex items-center justify-between gap-5 lg:gap-7.5 py-2.5 lg:py-3.5 border-b border-gray-200">
                                            <div>
                                                <h4 className="text-lg font-medium">
                                                    {`${familyMember.full_name} - ${familyMember.email_address} - ${familyMember.phone_number || '(no phone number)'}`}
                                                </h4>
                                            </div>
                                            <div>
                                                <button className="btn btn-secondary" onClick={() => {
                                                    onResendFamilyMemberInvitation(familyMember);
                                                }}>
                                                    Re-Send
                                                </button>
                                            </div>
                                        </div>
                                    })}
                                </div>
                            </>}
                            <div>
                                <Link className="btn btn-lg btn-primary" to={`/services/${service.unique_identifier}/add-family-member`}>
                                    Add Family Member
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </>
}